(() => {
  const selector = '[data-thp-cart-rewards]';
  const rewardProperty = '_thp_threshold_gift';
  const celebrationStateKey = 'thp-cart-rewards-celebration-state';
  const confettiColors = ['#f2c6cd', '#f2d27a', '#9dcfc0', '#aebfe3', '#d8b9df', '#1a1a1a'];
  let syncing = false;
  let queued = false;

  function getConfiguration() {
    const element = document.querySelector(selector);
    if (!element) return null;

    return {
      root: element.dataset.cartRoot || '/',
      country: element.dataset.country || '',
      supported: element.dataset.supportedRegion === 'true',
      variantId: Number(element.dataset.giftVariantId || 0),
      available: element.dataset.giftAvailable === 'true',
      threshold: Number(element.dataset.giftThreshold || 0),
      shippingThreshold: Number(element.dataset.shippingThreshold || 0),
      subtotal: Number(element.dataset.qualifyingSubtotal || 0),
    };
  }

  function getCelebrationState() {
    try {
      return JSON.parse(window.sessionStorage.getItem(celebrationStateKey) || 'null');
    } catch (error) {
      return null;
    }
  }

  function setCelebrationState(state) {
    try {
      window.sessionStorage.setItem(celebrationStateKey, JSON.stringify(state));
    } catch (error) {
      // Celebration state is optional when session storage is unavailable.
    }
  }

  function burstConfetti(root, milestone) {
    if (!root || !milestone || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    document.querySelector('.thp-cart-rewards__confetti')?.remove();

    const layer = document.createElement('span');
    const milestoneRect = milestone.getBoundingClientRect();
    const originX = milestoneRect.left + milestoneRect.width / 2;
    const originY = milestoneRect.top + milestoneRect.height / 2;

    layer.className = 'thp-cart-rewards__confetti';
    layer.setAttribute('aria-hidden', 'true');

    for (let index = 0; index < 24; index += 1) {
      const piece = document.createElement('span');
      const angle = (Math.PI * 2 * index) / 24 + (Math.random() - 0.5) * 0.3;
      const distance = 42 + Math.random() * 58;

      piece.className = 'thp-cart-rewards__confetti-piece';
      piece.style.setProperty('--thp-confetti-origin-x', `${originX}px`);
      piece.style.setProperty('--thp-confetti-origin-y', `${originY}px`);
      piece.style.setProperty('--thp-confetti-x', `${Math.cos(angle) * distance}px`);
      piece.style.setProperty('--thp-confetti-y', `${Math.sin(angle) * distance + 22}px`);
      piece.style.setProperty('--thp-confetti-rotation', `${Math.round(Math.random() * 540 - 270)}deg`);
      piece.style.setProperty('--thp-confetti-delay', `${Math.round(Math.random() * 90)}ms`);
      piece.style.setProperty('--thp-confetti-color', confettiColors[index % confettiColors.length]);
      layer.appendChild(piece);
    }

    document.body.appendChild(layer);
    window.setTimeout(() => layer.remove(), 1100);
  }

  function celebrateNewMilestones(config, subtotal) {
    const root = document.querySelector(selector);
    if (!root || !config.supported) return;

    const nextState = {
      country: config.country,
      gift: subtotal >= config.threshold,
      shipping: subtotal >= config.shippingThreshold,
    };
    const previousState = getCelebrationState();

    setCelebrationState(nextState);

    if (!previousState || previousState.country !== config.country) return;

    if (!previousState.shipping && nextState.shipping) {
      burstConfetti(root, root.querySelector('.thp-cart-rewards__milestone--shipping'));
    } else if (!previousState.gift && nextState.gift) {
      burstConfetti(root, root.querySelector('.thp-cart-rewards__milestone:not(.thp-cart-rewards__milestone--shipping)'));
    }
  }

  function endpoint(root, path) {
    return `${root.replace(/\/$/, '')}/${path}`;
  }

  async function request(url, options = {}) {
    const response = await fetch(url, {
      ...options,
      headers: {
        Accept: 'application/json',
        ...(options.body ? { 'Content-Type': 'application/json' } : {}),
        ...options.headers,
      },
    });

    if (!response.ok) throw new Error(`Cart request failed with status ${response.status}`);
    return response.json();
  }

  function refreshCart() {
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));

    if (window.location.pathname.replace(/\/$/, '') === '/cart') {
      window.location.reload();
    }
  }

  async function sync() {
    if (syncing) {
      queued = true;
      return;
    }

    const config = getConfiguration();
    if (!config || !config.variantId) return;

    syncing = true;

    try {
      const cart = await request(endpoint(config.root, 'cart.js'));
      const qualifyingSubtotal = Number(cart.items_subtotal_price || 0);
      const qualifies = config.supported && qualifyingSubtotal >= config.threshold;
      const matchingItems = cart.items.filter((item) => Number(item.variant_id) === config.variantId);

      celebrateNewMilestones(config, qualifyingSubtotal);

      if (!qualifies) {
        if (matchingItems.length) {
          const updates = Object.fromEntries(matchingItems.map((item) => [item.key, 0]));
          await request(endpoint(config.root, 'cart/update.js'), {
            method: 'POST',
            body: JSON.stringify({ updates }),
          });
          refreshCart();
        }
        return;
      }

      if (!matchingItems.length) {
        if (!config.available) return;

        await request(endpoint(config.root, 'cart/add.js'), {
          method: 'POST',
          body: JSON.stringify({
            id: config.variantId,
            quantity: 1,
            properties: {
              [rewardProperty]: 'true',
              _thp_reward_country: config.country,
            },
          }),
        });
        refreshCart();
        return;
      }

      const keeper = matchingItems.find((item) => item.properties?.[rewardProperty] === 'true') || matchingItems[0];
      const updates = {};

      matchingItems.forEach((item) => {
        const desiredQuantity = item.key === keeper.key ? 1 : 0;
        if (Number(item.quantity) !== desiredQuantity) updates[item.key] = desiredQuantity;
      });

      if (Object.keys(updates).length) {
        await request(endpoint(config.root, 'cart/update.js'), {
          method: 'POST',
          body: JSON.stringify({ updates }),
        });
        refreshCart();
      }
    } catch (error) {
      console.error('[THP Cart Rewards]', error);
    } finally {
      syncing = false;

      if (queued) {
        queued = false;
        window.setTimeout(sync, 100);
      }
    }
  }

  function scheduleSync() { window.setTimeout(sync, 120); }

  document.addEventListener('DOMContentLoaded', scheduleSync);
  document.addEventListener('variant:add', scheduleSync);
  document.addEventListener('cart-drawer:refreshed', scheduleSync);
  document.addEventListener('dialog:after-show', scheduleSync);
  document.documentElement.addEventListener('cart:change', scheduleSync);
  document.documentElement.addEventListener('cart:refresh', scheduleSync);

  window.ThpCartRewards = { sync };
})();
